import React, { CSSProperties, useCallback } from 'react';
import { GridList, MenuItem } from '@material-ui/core';

import { SelectWithFilterPaperProps } from './SelectWithFilterPaper.interfaces';
import { getSuggestions } from 'app/shared/helpers/components';
import { Dictionary, DictionaryEntry } from 'app/services/dict.service';
import { AnswerGroup } from 'app/shared/interfaces/AnswerGroup.interface';
import { SelectOptionsPaper } from 'app/components/SelectOptionsPaper';
import { useStyles } from './SelectWithFilterPaper.styles';

const SelectWithFilterPaper = (props: SelectWithFilterPaperProps) => {
  const {
    inputValue,
    dictionary,
    cols,
    answerGroups,
    getItemProps,
    selected,
  } = props;

  const classes = useStyles(props);

  const suggestions = getSuggestions(dictionary, inputValue);

  const getStyle = useCallback(() => {
    return {
      minWidth: '100%',
      width: 'fit-content',
    } as CSSProperties;
  }, []);

  const renderMenuItemsInCols = (suggestions: Dictionary) => {
    return (
      <GridList cellHeight={42} spacing={0} cols={cols}>
        {renderMenuItems(suggestions)}
      </GridList>
    );
  };

  const renderMenuItems = (suggestions: Dictionary) => {
    if (answerGroups) {
      return renderGroupedItems(answerGroups, suggestions);
    } else {
      return suggestions.map((suggestion, index) =>
        renderMenuItem(suggestion, index)
      );
    }
  };

  const renderGroupedItems = (
    answerGroups: Array<AnswerGroup>,
    suggestions: Dictionary
  ) => {
    const itemsWithoutGroup = suggestions
      .filter((item) => !item.group)
      .map((suggestion, index) => renderMenuItem(suggestion, index));

    const itemsWithGroup = answerGroups
      .filter((entry) =>
        suggestions.find((item) => item.group === entry.groupName)
      )
      .map((group, index) => (
        <React.Fragment key={index}>
          <p className={classes.groupHeader}>{group.label}</p>
          {suggestions.map(
            (suggestion, key) =>
              group.groupName === suggestion.group &&
              renderMenuItem(suggestion, key)
          )}
        </React.Fragment>
      ));

    return itemsWithoutGroup.concat(itemsWithGroup);
  };

  const renderMenuItem = (suggestion: DictionaryEntry, index: number) => {
    const disabled = !!suggestion.disabled;

    return (
      <MenuItem
        {...getItemProps({
          item: suggestion,
          index,
          key: suggestion.key,
          selected: index === selected,
        })}
        key={suggestion.key}
        classes={{ root: classes.menuItem, selected: classes.selected }}
        className={cols > 1 ? classes.multiItems : undefined}
        disabled={disabled}
        tabIndex={-1}
      >
        {suggestion.subLabels
          ? getTitleWithSubtitle(suggestion)
          : suggestion.value}
      </MenuItem>
    );
  };

  const getTitleWithSubtitle = (suggestion: DictionaryEntry) => {
    return (
      <>
        <span className={classes.menuItemTitle}>{suggestion.value}</span>
        <span className={classes.menuItemSubtitle}>
          {suggestion.subLabels &&
            suggestion.subLabels.map((subLabel) => (
              <span key={subLabel}>{subLabel}</span>
            ))}
        </span>
      </>
    );
  };

  return suggestions.length ? (
    <SelectOptionsPaper className={classes.root} style={getStyle()}>
      {cols > 1
        ? renderMenuItemsInCols(suggestions)
        : renderMenuItems(suggestions)}
    </SelectOptionsPaper>
  ) : null;
};

export default SelectWithFilterPaper;
